// services/CommentService.js - Service for handling comment management operations
import { collection, addDoc, getDocs, query, orderBy, where, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

class CommentService {
  constructor() {
    this.commentsCollection = 'comments';
    this.repliesCollection = 'comment_replies';
  }

  /**
   * Get all comments with their replies
   */
  async getAllComments() {
    try {
      // Query comments ordered by creation date (newest first)
      const commentsQuery = query(
        collection(db, this.commentsCollection),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );
      
      const commentsSnapshot = await getDocs(commentsQuery);
      const comments = [];
      
      // Process each comment
      for (const commentDoc of commentsSnapshot.docs) {
        const commentData = {
          id: commentDoc.id,
          ...commentDoc.data()
        };
        
        // Get replies for this comment
        const repliesQuery = query(
          collection(db, this.repliesCollection),
          where('commentId', '==', commentDoc.id),
          orderBy('createdAt', 'asc')
        );
        
        const repliesSnapshot = await getDocs(repliesQuery);
        const replies = [];
        
        repliesSnapshot.forEach((replyDoc) => {
          replies.push({
            id: replyDoc.id,
            ...replyDoc.data()
          });
        });
        
        commentData.replies = replies;
        comments.push(commentData);
      }

      return {
        success: true,
        data: comments
      };
    } catch (error) {
      console.error('Error fetching comments:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch comments',
        data: []
      };
    }
  }

  /**
   * Delete a comment and all its replies
   */
  async deleteComment(commentId) {
    try {
      // First, delete all replies for this comment
      const repliesQuery = query(
        collection(db, this.repliesCollection),
        where('commentId', '==', commentId)
      );
      
      const repliesSnapshot = await getDocs(repliesQuery);
      
      // Delete each reply
      for (const replyDoc of repliesSnapshot.docs) {
        await deleteDoc(doc(db, this.repliesCollection, replyDoc.id));
      }
      
      // Then delete the comment itself
      await deleteDoc(doc(db, this.commentsCollection, commentId));

      return {
        success: true,
        message: 'Comment and replies deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting comment:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete comment'
      };
    }
  }

  /**
   * Add a user reply to a comment
   */
  async addUserReply(commentId, replyText, userName, userEmail = '') {
    try {
      // Validate inputs
      if (!commentId || !replyText || !userName) {
        return {
          success: false,
          error: 'Missing required fields'
        };
      }

      if (replyText.trim().length < 1) {
        return {
          success: false,
          error: 'Reply text cannot be empty'
        };
      }

      if (replyText.length > 500) {
        return {
          success: false,
          error: 'Reply text is too long (max 500 characters)'
        };
      }



      // Check if comment exists
      const commentRef = doc(db, this.commentsCollection, commentId);
      const commentDoc = await getDoc(commentRef);
      
      if (!commentDoc.exists()) {
        return {
          success: false,
          error: 'Comment not found'
        };
      }

      const reply = {
        commentId,
        text: replyText.trim(),
        userName: userName.trim(),
        userEmail: userEmail.trim(),
        replyType: 'user', // Distinguish from admin replies
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.repliesCollection), reply);

      return {
        success: true,
        replyId: docRef.id,
        message: 'Reply added successfully'
      };
    } catch (error) {
      console.error('Error adding user reply:', error);
      return {
        success: false,
        error: error.message || 'Failed to add reply'
      };
    }
  }

  /**
   * Add a reply to a comment (admin version)
   */
  async addReply(commentId, replyText, adminName) {
    try {
      // Validate inputs
      if (!commentId || !replyText || !adminName) {
        return {
          success: false,
          error: 'Missing required fields'
        };
      }

      if (replyText.trim().length < 1) {
        return {
          success: false,
          error: 'Reply text cannot be empty'
        };
      }

      if (replyText.length > 1000) {
        return {
          success: false,
          error: 'Reply text is too long (max 1000 characters)'
        };
      }

      // Check if comment exists
      const commentRef = doc(db, this.commentsCollection, commentId);
      const commentDoc = await getDoc(commentRef);
      
      if (!commentDoc.exists()) {
        return {
          success: false,
          error: 'Comment not found'
        };
      }

      const reply = {
        commentId,
        text: replyText.trim(),
        adminName,
        replyType: 'admin', // Distinguish from user replies
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.repliesCollection), reply);

      return {
        success: true,
        replyId: docRef.id,
        message: 'Reply added successfully'
      };
    } catch (error) {
      console.error('Error adding reply:', error);
      return {
        success: false,
        error: error.message || 'Failed to add reply'
      };
    }
  }

  /**
   * Get comments for a specific brand
   */
  async getBrandComments(brandId) {
    try {
      console.log('CommentService: Fetching comments for brandId:', brandId);
      
      // Simplified query - just filter by brandId first, then filter and sort in memory
      const commentsQuery = query(
        collection(db, this.commentsCollection),
        where('relatedContentId', '==', brandId)
      );
      
      const commentsSnapshot = await getDocs(commentsQuery);
      console.log('CommentService: Found', commentsSnapshot.docs.length, 'comments');
      
      const comments = [];
      
      // Process each comment
      for (const commentDoc of commentsSnapshot.docs) {
        const commentData = {
          id: commentDoc.id,
          ...commentDoc.data()
        };
        
        // Only include active comments
        if (commentData.status === 'active') {
          // Get replies for this comment
          try {
            const repliesQuery = query(
              collection(db, this.repliesCollection),
              where('commentId', '==', commentDoc.id)
            );
            
            const repliesSnapshot = await getDocs(repliesQuery);
            const replies = [];
            
            repliesSnapshot.forEach((replyDoc) => {
              replies.push({
                id: replyDoc.id,
                ...replyDoc.data()
              });
            });
            
            // Sort replies by creation date (oldest first)
            replies.sort((a, b) => {
              if (a.createdAt && b.createdAt) {
                return a.createdAt.toDate() - b.createdAt.toDate();
              }
              return 0;
            });
            
            commentData.replies = replies;
          } catch (replyError) {
            console.error('Error fetching replies for comment', commentDoc.id, ':', replyError);
            commentData.replies = [];
          }
          
          comments.push(commentData);
        }
      }

      // Sort comments by creation date (newest first)
      comments.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate() - a.createdAt.toDate();
        }
        return 0;
      });

      console.log('CommentService: Returning', comments.length, 'active comments');
      return {
        success: true,
        data: comments
      };
    } catch (error) {
      console.error('Error fetching brand comments:', error);
      
      // If the specific query fails, try a fallback approach
      console.log('CommentService: Primary query failed, trying fallback...');
      try {
        // Fallback: Get all comments and filter client-side
        const allCommentsQuery = query(collection(db, this.commentsCollection));
        const allCommentsSnapshot = await getDocs(allCommentsQuery);
        
        const comments = [];
        allCommentsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.relatedContentId === brandId && data.status === 'active') {
            comments.push({
              id: doc.id,
              ...data,
              replies: [] // We'll skip replies in fallback for simplicity
            });
          }
        });

        // Sort by creation date
        comments.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return b.createdAt.toDate() - a.createdAt.toDate();
          }
          return 0;
        });

        console.log('CommentService: Fallback found', comments.length, 'comments');
        return {
          success: true,
          data: comments
        };
      } catch (fallbackError) {
        console.error('CommentService: Fallback also failed:', fallbackError);
        return {
          success: false,
          error: error.message || 'Failed to fetch comments',
          data: []
        };
      }
    }
  }

  /**
   * Add a new comment from a user
   */
  async addComment(brandId, brandName, commentText, authorName, authorEmail = '') {
    try {
      // Validate inputs
      if (!brandId || !commentText || !authorName) {
        return {
          success: false,
          error: 'Missing required fields'
        };
      }

      if (commentText.trim().length < 1) {
        return {
          success: false,
          error: 'Comment text cannot be empty'
        };
      }

      if (commentText.length > 1000) {
        return {
          success: false,
          error: 'Comment text is too long (max 1000 characters)'
        };
      }



      const comment = {
        text: commentText.trim(),
        authorName: authorName.trim(),
        authorEmail: authorEmail.trim(),
        relatedContent: brandName,
        relatedContentId: brandId,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active'
      };

      const docRef = await addDoc(collection(db, this.commentsCollection), comment);

      return {
        success: true,
        commentId: docRef.id,
        message: 'Comment added successfully'
      };
    } catch (error) {
      console.error('Error adding comment:', error);
      return {
        success: false,
        error: error.message || 'Failed to add comment'
      };
    }
  }

  /**
   * Debug method to check if comments exist in database
   */
  async debugCheckComments() {
    try {
      const allCommentsQuery = query(collection(db, this.commentsCollection));
      const snapshot = await getDocs(allCommentsQuery);
      
      console.log('=== DEBUG: All Comments in Database ===');
      console.log('Total comments found:', snapshot.docs.length);
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Comment:', {
          id: doc.id,
          relatedContentId: data.relatedContentId,
          status: data.status,
          authorName: data.authorName,
          text: data.text?.substring(0, 50) + '...',
          createdAt: data.createdAt
        });
      });
      
      return snapshot.docs.length;
    } catch (error) {
      console.error('Debug check failed:', error);
      return 0;
    }
  }

  /**
   * Format date for display
   */
  formatDate(date) {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  }
}

// Create and export a singleton instance
const commentService = new CommentService();
export default commentService;